// Finance Books — Inventory section (stock summary + movements + adjustments).
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import fb from '../api';
import { PageHeader, Btn, Drawer, Input, Select, Table, Pill, fmtMoney, fmtDate } from '../ui';

export default function Inventory() {
  const [tab, setTab] = useState('summary');
  const [summary, setSummary] = useState([]);
  const [moves, setMoves] = useState([]);
  const [adjOpen, setAdjOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [adjForm, setAdjForm] = useState({ item_id: '', qty_delta: 0, reason: '' });

  const reload = async () => {
    setSummary(await fb.get('/stock/summary'));
    setMoves(await fb.get('/stock/movements'));
    setItems(await fb.get('/items'));
  };
  useEffect(() => { reload(); }, []);

  const saveAdjust = async () => {
    try {
      if (!adjForm.item_id || !adjForm.reason) return toast.error('Item and reason required');
      const payload = { ...adjForm, qty_delta: Number(adjForm.qty_delta) };
      await fb.post('/stock/adjust', payload);
      toast.success('Adjustment posted · journal entry created');
      setAdjOpen(false); setAdjForm({ item_id: '', qty_delta: 0, reason: '' }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="fb-inventory">
      <PageHeader title="Inventory" subtitle="Stock on hand · movements · adjustments"
        right={<Btn variant="accent" onClick={() => setAdjOpen(true)} data-testid="fb-stock-adjust">+ Stock Adjustment</Btn>} />

      <div className="flex gap-2 mb-4">
        {[{ k: 'summary', l: 'Stock Summary' }, { k: 'moves', l: 'Stock Movements' }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-3 py-1.5 rounded text-xs tracking-[0.15em] uppercase ${tab === t.k ? 'bg-white text-neutral-900' : 'text-white/55 hover:text-white border border-white/10'}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'summary' && (
        <Table
          columns={[
            { key: 'sku', label: 'SKU', render: (r) => <span className="font-mono text-xs text-white/60">{r.sku}</span> },
            { key: 'name', label: 'Item' },
            { key: 'hsn_sac', label: 'HSN' },
            { key: 'stock_on_hand', label: 'On Hand', align: 'right', render: (r) => <span className={r.below_reorder ? 'text-orange-300' : 'text-emerald-300'}>{r.stock_on_hand}</span> },
            { key: 'reorder_level', label: 'Reorder', align: 'right' },
            { key: 'purchase_price', label: 'Avg Cost', align: 'right', render: (r) => fmtMoney(r.purchase_price) },
            { key: 'stock_value', label: 'Value', align: 'right', render: (r) => <span className="tabular-nums font-medium">{fmtMoney(r.stock_value)}</span> },
          ]}
          rows={summary} empty="No tracked items yet."
        />
      )}

      {tab === 'moves' && (
        <Table
          columns={[
            { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
            { key: 'doc_no', label: 'Doc #', render: (r) => <span className="font-mono text-xs text-white/60">{r.doc_no}</span> },
            { key: 'item_name', label: 'Item' },
            { key: 'type', label: 'Type', render: (r) => <Pill tone={r.qty > 0 ? 'green' : 'red'}>{r.type}</Pill> },
            { key: 'qty', label: 'Qty', align: 'right', render: (r) => <span className={r.qty > 0 ? 'text-emerald-300' : 'text-rose-300'}>{r.qty}</span> },
            { key: 'rate', label: 'Rate', align: 'right', render: (r) => fmtMoney(r.rate) },
          ]}
          rows={moves} empty="No stock movements yet."
        />
      )}

      <Drawer open={adjOpen} onClose={() => setAdjOpen(false)} title="Stock Adjustment" width="max-w-lg">
        <div className="space-y-3">
          <Select label="Item *" value={adjForm.item_id} onChange={(e) => setAdjForm({ ...adjForm, item_id: e.target.value })}>
            <option value="">— Select —</option>
            {items.filter((i) => i.track_inventory).map((i) => <option key={i.id} value={i.id}>{i.name} (stock {i.stock_on_hand})</option>)}
          </Select>
          <Input label="Quantity Delta * (+ to add, - to remove)" type="number" value={adjForm.qty_delta} onChange={(e) => setAdjForm({ ...adjForm, qty_delta: e.target.value })} />
          <Input label="Reason *" value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} placeholder="e.g. Damage, Stock count correction" />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setAdjOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={saveAdjust} data-testid="adj-save">Post Adjustment</Btn>
        </div>
      </Drawer>
    </div>
  );
}
